/**
 * SAAS-PORTAL — cadastro público de empresa (FASE 2).
 *
 * Formulário PÚBLICO (sem sessão): empresa, responsável, e-mail, telefone
 * (opcional) e senha → POST /api/signup. O backend cria um tenant PENDENTE +
 * dono INATIVO; nada fica utilizável até a aprovação de um super_admin. A
 * resposta é sempre neutra (anti-enumeração), então mostramos a MESMA mensagem
 * de "aguardando aprovação" independentemente de o e-mail já existir.
 */
import { useState } from "react";
import { signup as apiSignup } from "../lib/api.js";
import { loadConfig, saveConfig } from "../lib/config.js";

export default function SignupPage({ onBack }) {
  const cfg = loadConfig();
  const [company, setCompany] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [apiUrl, setApiUrl] = useState(cfg.apiUrl);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!company.trim() || !email.trim() || !password) {
      setError("Preencha empresa, e-mail e senha.");
      return;
    }
    if (password.length < 8) {
      setError("A senha deve ter ao menos 8 caracteres.");
      return;
    }
    setBusy(true);
    try {
      saveConfig({ apiUrl });
      await apiSignup({
        company_name: company.trim(),
        owner_full_name: ownerName.trim() || undefined,
        owner_email: email.trim(),
        contact_phone: phone.trim() || undefined,
        password,
      });
      setDone(true);
    } catch (err) {
      setError(err?.message || "Falha ao enviar o cadastro.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ipwd-shell min-h-screen flex items-center justify-center bg-noc-bg px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-noc-text">IP WATCHDOG</h1>
          <p className="mt-1.5 font-mono text-[10px] tracking-[0.22em] text-noc-accent">
            OPERATIONS CLOUD · NOVA EMPRESA
          </p>
        </div>

        {done ? (
          <div className="glass-card p-7 space-y-5 text-center">
            <div className="text-3xl" aria-hidden="true">✅</div>
            <h2 className="text-base font-semibold text-noc-text">
              Conta criada e aguardando aprovação
            </h2>
            <p className="text-sm text-noc-muted">
              Recebemos seu cadastro. Assim que a equipe aprovar sua empresa, você
              poderá acessar o portal com o e-mail e a senha informados.
            </p>
            <button type="button" onClick={() => onBack?.()} className="btn-primary w-full h-11">
              Voltar ao login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="glass-card p-6 space-y-5">
            <div className="space-y-1">
              <label className="label">Nome da empresa *</label>
              <input value={company} onChange={(e) => setCompany(e.target.value)}
                className="w-full h-11 px-4 rounded-xl bg-noc-bg/60 border border-noc-border text-noc-text focus:border-noc-accent/60 outline-none"
                placeholder="Minha Empresa Ltda" maxLength={120} />
            </div>
            <div className="space-y-1">
              <label className="label">Responsável</label>
              <input value={ownerName} onChange={(e) => setOwnerName(e.target.value)}
                className="w-full h-11 px-4 rounded-xl bg-noc-bg/60 border border-noc-border text-noc-text focus:border-noc-accent/60 outline-none"
                placeholder="Seu nome" maxLength={120} />
            </div>
            <div className="space-y-1">
              <label className="label">E-mail *</label>
              <input type="email" autoComplete="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-11 px-4 rounded-xl bg-noc-bg/60 border border-noc-border text-noc-text focus:border-noc-accent/60 outline-none"
                placeholder="voce@empresa.com" maxLength={254} />
            </div>
            <div className="space-y-1">
              <label className="label">Telefone</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)}
                className="w-full h-11 px-4 rounded-xl bg-noc-bg/60 border border-noc-border text-noc-text focus:border-noc-accent/60 outline-none"
                placeholder="+55 11 99999-0000" maxLength={40} />
            </div>
            <div className="space-y-1">
              <label className="label">Senha *</label>
              <input type="password" autoComplete="new-password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-11 px-4 rounded-xl bg-noc-bg/60 border border-noc-border text-noc-text focus:border-noc-accent/60 outline-none"
                placeholder="mínimo 8 caracteres" />
            </div>
            <div className="space-y-1">
              <label className="label">API URL (backend)</label>
              <input value={apiUrl} onChange={(e) => setApiUrl(e.target.value)}
                className="w-full h-11 px-4 rounded-xl bg-noc-bg/60 border border-noc-border text-noc-text focus:border-noc-accent/60 outline-none font-mono text-sm"
                spellCheck={false} />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-noc-err bg-noc-err/10 border border-noc-err/30 rounded-lg px-3 py-2">
                <span className="led-err" />
                {error}
              </div>
            )}

            <button type="submit" disabled={busy} className="btn-primary w-full h-11">
              {busy ? "Enviando…" : "Criar conta"}
            </button>
            <div className="text-center">
              <button type="button" onClick={() => onBack?.()}
                className="text-xs text-noc-muted hover:text-noc-text underline">
                Já tenho conta — entrar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
